<template>
  <div
    class="flex flex-wrap md:flex-nowrap text-white justify-between w-full mb-10 gap-4"
  >
    <BillingOverviewCell>
      <template #header>Current plan</template>
      <template #icon><CreditCard :size="16" /></template>
      {{ subscription.name }}
    </BillingOverviewCell>
    <BillingOverviewCell>
      <template #header>Total seats</template>
      <template #icon><Users :size="16" /></template>
      {{ totalBillableUsers }}
      <template v-if="subscription.name !== 'Free'">
        / {{ subscriptionsSeats }}&nbsp;
      </template>
      <RouterLink :to="{ name: 'workspace-people' }">view</RouterLink>
      <Tooltip
        v-if="subscription.billing_cycle === 'yearly'"
        side="bottom"
        :side-offset="8"
      >
        <Info class="size-5 inline align-bottom ml-2" />
        <template #content>
          Monthly Subscription Seats: {{ monthlySubscriptionSeats }}<br />
          Yearly Subscription Seats: {{ yearlySubscriptionSeats }}
        </template>
      </Tooltip>
    </BillingOverviewCell>
    <BillingOverviewCell>
      <template #header>Next invoice</template>
      <template #icon><Newspaper :size="16" /></template>
      <template v-if="isLoading"><Spinner size="size-4" /></template>
      <template v-else-if="nextInvoices?.monthly?.date">
        {{ nextInvoices?.monthly?.date }}
        <Tooltip
          v-if="subscription.billing_cycle === 'yearly'"
          side="bottom"
          :side-offset="8"
        >
          <Info class="size-5 inline align-bottom ml-1" />
          <template #content>
            Next Monthly Invoice: {{ nextInvoices.monthly.date }}
            <template v-if="nextInvoices.yearly.date">
              <br />Next Yearly Invoice: {{ nextInvoices.yearly.date }}
            </template>
          </template>
        </Tooltip>
      </template>
      <template v-else>-</template>
    </BillingOverviewCell>
    <BillingOverviewCell>
      <template #header>Invoice Total</template>
      <template #icon><CircleDollarSign :size="16" /></template>
      <template v-if="isLoading"><Spinner size="size-4" /></template>
      <template v-else-if="nextInvoices?.monthly?.amount">
        {{ nextInvoices?.monthly?.amount }}
        <Tooltip
          v-if="subscription.billing_cycle === 'yearly'"
          side="bottom"
          :side-offset="8"
        >
          <Info class="size-5 inline align-bottom ml-1" />
          <template #content>
            Next Monthly Invoice: {{ nextInvoices.monthly.amount }}
            <template v-if="nextInvoices.yearly.amount">
              <br />Next Yearly Invoice: {{ nextInvoices.yearly.amount }}
            </template>
          </template>
        </Tooltip>
      </template>
      <template v-else>-</template>
    </BillingOverviewCell>
  </div>
  <div v-if="coupons" class="mb-5">
    <h2 class="text-lg text-white">Discount</h2>
    <span class="text-xs text-accent-200">
      {{ coupons }}
    </span>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useQuery } from '@tanstack/vue-query';
import { RouterLink } from 'vue-router';

import {
  CreditCard,
  Newspaper,
  Users,
  CircleDollarSign,
  Info,
} from 'lucide-vue-next';

import requests from '@vuex/requests.js';
import Spinner from '@ui/components/Spinner.vue';
import { Tooltip } from '@ui/components/tooltip';
import BillingOverviewCell from './BillingOverviewCell.vue';

const store = useStore();

const subscription = computed(
  () =>
    store.getters['workspace/getCurrentSubscriptionPlan'] ?? { name: 'Free' }
);

const totalBillableUsers = computed(
  () => store.getters['workspace/getTotalBillableUsers']
);

const subscriptionsSeats = computed(
  () => store.getters['workspace/getSubscriptionsSeats']
);

const monthlySubscriptionSeats = computed(
  () => store.getters['workspace/getMonthlySubscriptionSeats']
);

const yearlySubscriptionSeats = computed(
  () => store.getters['workspace/getYearlySubscriptionSeats']
);

const { isLoading, data: nextInvoices } = useQuery({
  queryKey: ['workspaceNextInvoice'],
  queryFn: () =>
    requests.workspaces.subscription.nextInvoices(
      store.getters['workspace/getId']
    ),
});

const coupons = computed(() => {
  if (nextInvoices?.value?.monthly?.coupons) {
    const couponsText = nextInvoices.value.monthly.coupons
      .map(coupon => {
        if (coupon.is_percentage) {
          return `${coupon.name} (${coupon.discount}% off)`;
        }
        return `${coupon.name} ($${coupon.discount.toFixed(2)} off)`;
      })
      .join(', ');

    return couponsText;
  }
  return null;
});
</script>

<style lang="scss" scoped></style>
